import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { adminRolesAPI, adminTenantUsersAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const Section = styled.div`
  padding: 2rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.35rem 0;
  letter-spacing: -0.025em;
`;

const PageSubtitle = styled.p`
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  padding: 1.75rem;
  margin-bottom: 1.75rem;
`;

const CardTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
  vertical-align: middle;
`;

const Tr = styled.tr`
  &:hover {
    background: #fafafa;
  }
`;

const Button = styled.button`
  background: #043663;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover {
    background: #032a4a;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ButtonSecondary = styled(Button)`
  background: #f3f4f6;
  color: #374151;
  &:hover {
    background: #e5e7eb;
  }
`;

const ButtonDanger = styled(Button)`
  background: #dc2626;
  &:hover {
    background: #b91c1c;
  }
`;

const ButtonSmall = styled(Button)`
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  width: 100%;
  max-width: 260px;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  min-width: 140px;
  &:focus {
    outline: none;
    border-color: #043663;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  align-items: end;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.8125rem;
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 1.5rem 0;
`;

const EmptyState = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  padding: 1rem 0;
  line-height: 1.5;
`;

const PermTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  margin-top: 1rem;
`;

const PermTh = styled.th`
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
  font-weight: 600;
  &:first-child {
    width: 40%;
  }
`;

const PermTd = styled.td`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
  vertical-align: middle;
`;

const CheckboxCell = styled.td`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: center;
  vertical-align: middle;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  input {
    width: 1rem;
    height: 1rem;
    accent-color: #043663;
    cursor: pointer;
  }
`;

const SectionLabel = styled.span`
  font-weight: 500;
  color: #374151;
`;

// Section keys, labels, and which actions each section actually supports (no generic CRUD for everything)
const SECTION_CONFIG = [
  { key: 'teams', label: 'Teams', actions: ['read', 'create', 'edit'] },
  { key: 'registration_analytics', label: 'Registration analytics', actions: ['read'] },
  { key: 'divisions', label: 'Divisions', actions: ['read'] },
  { key: 'division_lock', label: 'Division lock', actions: ['read'] },
  { key: 'league_management', label: 'League management', actions: ['read', 'edit'] },
  { key: 'promotion_relegation', label: 'Promotion & relegation', actions: ['read', 'edit'] },
  { key: 'home_away_assignment', label: 'Home / away assignment', actions: ['read', 'edit'] },
  { key: 'promo_codes', label: 'Promo codes', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'partner_communications', label: 'Partner communications', actions: ['read'] },
  { key: 'teammate_finder', label: 'Teammate finder', actions: ['read', 'edit'] },
  { key: 'settings_general', label: 'Settings – General', actions: ['read', 'edit'] },
  { key: 'settings_seasons', label: 'Settings – Phases', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'settings_promo_codes', label: 'Settings – Promo codes', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'settings_tournaments', label: 'Settings – Tournaments', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'shop', label: 'Shop', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'organised_events', label: 'Events (bookable tournaments)', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'matches', label: 'Matches', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'scheduled_matches', label: 'Scheduled matches', actions: ['read'] },
  { key: 'stats', label: 'Stats & overview', actions: ['read'] },
  { key: 'admin_users_and_roles', label: 'Users & roles', actions: ['read', 'create', 'edit', 'delete'] },
  { key: 'audit_log', label: 'Audit log', actions: ['read'] },
];

const SECTION_KEYS = SECTION_CONFIG.map((s) => s.key);

const ACTION_LABELS = {
  read: 'Access',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
};

const defaultPermissions = () => {
  const p = {};
  SECTION_CONFIG.forEach(({ key, actions }) => {
    p[key] = {};
    actions.forEach((a) => { p[key][a] = false; });
  });
  return p;
};

/** Normalize permissions for API: only include actions that each section supports */
const normalizePermissionsForApi = (perms) => {
  const out = {};
  SECTION_CONFIG.forEach(({ key, actions }) => {
    if (!perms[key]) { out[key] = {}; return; }
    out[key] = {};
    actions.forEach((a) => { out[key][a] = !!perms[key][a]; });
  });
  return out;
};

export default function AdminUsersAndRolesSection() {
  const queryClient = useQueryClient();
  const { adminUser } = useAdminAuth();
  const canCreate = adminUser?.isSuperAdmin || adminUser?.permissions?.admin_users_and_roles?.create;
  const canEdit = adminUser?.isSuperAdmin || adminUser?.permissions?.admin_users_and_roles?.edit;
  const canDelete = adminUser?.isSuperAdmin || adminUser?.permissions?.admin_users_and_roles?.delete;

  const { data: rolesRes, isLoading: rolesLoading } = useQuery(
    'admin-roles',
    () => adminRolesAPI.getList(),
    { enabled: !!adminUser }
  );
  const { data: usersRes, isLoading: usersLoading } = useQuery(
    'admin-tenant-users',
    () => adminTenantUsersAPI.getList(),
    { enabled: !!adminUser }
  );
  const roles = rolesRes?.data ?? [];
  const users = usersRes?.data ?? [];

  const [roleName, setRoleName] = useState('');
  const [rolePermissions, setRolePermissions] = useState(defaultPermissions());
  const [editingRole, setEditingRole] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [userRoleId, setUserRoleId] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserRoleId, setEditUserRoleId] = useState('');
  const [editUserActive, setEditUserActive] = useState(true);

  const createRoleMutation = useMutation(
    (payload) => adminRolesAPI.create(payload),
    {
      onSuccess: () => {
        toast.success('Role created');
        setRoleName('');
        setRolePermissions(defaultPermissions());
        queryClient.invalidateQueries('admin-roles');
      },
      onError: (e) => toast.error(e?.response?.data?.message || 'Failed to create role'),
    }
  );
  const updateRoleMutation = useMutation(
    ({ id, data }) => adminRolesAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('Role updated');
        setEditingRole(null);
        queryClient.invalidateQueries('admin-roles');
      },
      onError: (e) => toast.error(e?.response?.data?.message || 'Failed to update role'),
    }
  );
  const deleteRoleMutation = useMutation(
    (id) => adminRolesAPI.delete(id),
    {
      onSuccess: () => {
        toast.success('Role deleted');
        queryClient.invalidateQueries('admin-roles');
      },
      onError: (e) => toast.error(e?.response?.data?.message || 'Failed to delete role'),
    }
  );
  const createUserMutation = useMutation(
    (payload) => adminTenantUsersAPI.create(payload),
    {
      onSuccess: () => {
        toast.success('User created');
        setUserEmail('');
        setUserPassword('');
        setUserFirstName('');
        setUserLastName('');
        setUserRoleId('');
        queryClient.invalidateQueries('admin-tenant-users');
      },
      onError: (e) => toast.error(e?.response?.data?.message || 'Failed to create user'),
    }
  );
  const updateUserMutation = useMutation(
    ({ id, data }) => adminTenantUsersAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('User updated');
        setEditingUser(null);
        queryClient.invalidateQueries('admin-tenant-users');
      },
      onError: (e) => toast.error(e?.response?.data?.message || 'Failed to update user'),
    }
  );

  const handleCreateRole = (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toast.error('Role name is required');
      return;
    }
    createRoleMutation.mutate({ name: roleName.trim(), permissions: normalizePermissionsForApi(rolePermissions) });
  };

  const handleUpdateRole = (e) => {
    e.preventDefault();
    if (!editingRole || !roleName.trim()) return;
    updateRoleMutation.mutate({ id: editingRole.id, data: { name: roleName.trim(), permissions: normalizePermissionsForApi(rolePermissions) } });
  };

  const handleCreateUser = (e) => {
    e.preventDefault();
    if (!userEmail.trim() || !userPassword || !userFirstName.trim() || !userLastName.trim()) {
      toast.error('Email, password, first name, and last name are required');
      return;
    }
    createUserMutation.mutate({
      email: userEmail.trim(),
      password: userPassword,
      first_name: userFirstName.trim(),
      last_name: userLastName.trim(),
      role_id: userRoleId || null,
    });
  };

  const handleStartEditUser = (u) => {
    setEditingUser(u);
    setEditUserName(`${u.first_name} ${u.last_name}`);
    setEditUserRoleId(u.role_id || '');
    setEditUserActive(u.is_active !== false);
  };

  const handleSaveEditUser = (e) => {
    e.preventDefault();
    if (!editingUser) return;
    const parts = editUserName.trim().split(/\s+/);
    const first = parts[0] || editingUser.first_name;
    const last = parts.length > 1 ? parts.slice(1).join(' ') : editingUser.last_name;
    updateUserMutation.mutate({
      id: editingUser.id,
      data: {
        first_name: first,
        last_name: last,
        role_id: editUserRoleId || null,
        is_active: editUserActive,
      },
    });
  };

  const togglePerm = (section, action) => {
    setRolePermissions((prev) => ({
      ...prev,
      [section]: { ...prev[section], [action]: !prev[section]?.[action] },
    }));
  };

  const ALL_ACTIONS = ['read', 'create', 'edit', 'delete'];

  const PermissionMatrix = () => (
    <PermTable>
      <thead>
        <tr>
          <PermTh>Section</PermTh>
          {ALL_ACTIONS.map((act) => (
            <PermTh key={act} style={{ textAlign: 'center' }}>{ACTION_LABELS[act] || act}</PermTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {SECTION_CONFIG.map(({ key, label, actions }) => (
          <tr key={key}>
            <PermTd>
              <SectionLabel>{label}</SectionLabel>
            </PermTd>
            {ALL_ACTIONS.map((act) => (
              <CheckboxCell key={act}>
                {actions.includes(act) ? (
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={rolePermissions[key]?.[act] || false}
                      onChange={() => togglePerm(key, act)}
                    />
                  </CheckboxLabel>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>—</span>
                )}
              </CheckboxCell>
            ))}
          </tr>
        ))}
      </tbody>
    </PermTable>
  );

  return (
    <Section>
      <PageHeader>
        <PageTitle>Users &amp; Roles</PageTitle>
        <PageSubtitle>
          Manage roles and their permissions, then assign roles to admin users. Users without a role have full access for this tenant.
        </PageSubtitle>
      </PageHeader>

      {/* Roles */}
      <Card>
        <CardTitle>Roles</CardTitle>
        <CardDescription>
          Each section only shows the actions it supports (e.g. Division lock = access only; Phases = create/edit/delete). Assign a role when creating or editing a user.
        </CardDescription>

        {rolesLoading ? (
          <EmptyState>Loading roles…</EmptyState>
        ) : (
          <>
            {roles.length === 0 && !editingRole && !(canCreate && !editingRole) ? (
              <EmptyState>No roles yet. Create one below to limit what certain admins can do. Users without a role have full access.</EmptyState>
            ) : (
              roles.length > 0 && (
                <Table>
                  <thead>
                    <tr>
                      <Th>Name</Th>
                      <Th style={{ width: '140px' }}></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((r) => (
                      <Tr key={r.id}>
                        <Td>{r.name}</Td>
                        <Td>
                          {canEdit && (
                            <ButtonSmall
                              type="button"
                              onClick={() => {
                                setEditingRole(r);
                                setRoleName(r.name);
                                setRolePermissions(r.permissions || defaultPermissions());
                              }}
                              style={{ marginRight: '0.5rem' }}
                            >
                              Edit
                            </ButtonSmall>
                          )}
                          {canDelete && (
                            <ButtonDanger
                              type="button"
                              className="small"
                              style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}
                              onClick={() => {
                                if (window.confirm('Delete this role? Users with this role will need to be reassigned.')) {
                                  deleteRoleMutation.mutate(r.id);
                                }
                              }}
                            >
                              Delete
                            </ButtonDanger>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )
            )}

            {canCreate && !editingRole && (
              <>
                <Divider />
                <CardTitle as="h3" style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>
                  Create role
                </CardTitle>
                <form onSubmit={handleCreateRole}>
                  <FormGrid>
                    <FormGroup>
                      <Label>Role name</Label>
                      <Input
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                        placeholder="e.g. League Manager"
                      />
                    </FormGroup>
                    <FormGroup style={{ alignSelf: 'flex-end' }}>
                      <Button type="submit" disabled={createRoleMutation.isLoading}>
                        {createRoleMutation.isLoading ? 'Creating…' : 'Create role'}
                      </Button>
                    </FormGroup>
                  </FormGrid>
                  <PermissionMatrix />
                </form>
              </>
            )}

            {editingRole && canEdit && (
              <>
                <Divider />
                <CardTitle as="h3" style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>
                  Edit role
                </CardTitle>
                <form onSubmit={handleUpdateRole}>
                  <FormGrid>
                    <FormGroup>
                      <Label>Role name</Label>
                      <Input value={roleName} onChange={(e) => setRoleName(e.target.value)} />
                    </FormGroup>
                    <FormGroup style={{ alignSelf: 'flex-end', flexDirection: 'row', gap: '0.5rem' }}>
                      <Button type="submit" disabled={updateRoleMutation.isLoading}>
                        {updateRoleMutation.isLoading ? 'Saving…' : 'Save'}
                      </Button>
                      <ButtonSecondary
                        type="button"
                        onClick={() => {
                          setEditingRole(null);
                          setRoleName('');
                          setRolePermissions(defaultPermissions());
                        }}
                      >
                        Cancel
                      </ButtonSecondary>
                    </FormGroup>
                  </FormGrid>
                  <PermissionMatrix />
                </form>
              </>
            )}
          </>
        )}
      </Card>

      {/* Users */}
      <Card>
        <CardTitle>Users</CardTitle>
        <CardDescription>
          Admin users for this tenant. Assign a role to limit access, or leave as “Full access” for full tenant access.
        </CardDescription>

        {usersLoading ? (
          <EmptyState>Loading users…</EmptyState>
        ) : (
          <>
            {users.length === 0 ? (
              <EmptyState>No admin users for this tenant yet. Add one below.</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th style={{ width: '100px' }}></Th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <Tr key={u.id}>
                      <Td>{u.first_name} {u.last_name}</Td>
                      <Td>{u.email}</Td>
                      <Td>{u.role_name || 'Full access'}</Td>
                      <Td>{u.is_active !== false ? 'Active' : 'Inactive'}</Td>
                      <Td>
                        {editingUser?.id === u.id ? (
                          <form onSubmit={handleSaveEditUser} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                            <Input
                              value={editUserName}
                              onChange={(e) => setEditUserName(e.target.value)}
                              placeholder="Full name"
                              style={{ width: '140px' }}
                            />
                            <Select value={editUserRoleId} onChange={(e) => setEditUserRoleId(e.target.value)}>
                              <option value="">Full access</option>
                              {roles.map((r) => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </Select>
                            <Label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.35rem', margin: 0 }}>
                              <input
                                type="checkbox"
                                checked={editUserActive}
                                onChange={(e) => setEditUserActive(e.target.checked)}
                                style={{ accentColor: '#043663' }}
                              />
                              Active
                            </Label>
                            <ButtonSmall type="submit" disabled={updateUserMutation.isLoading}>
                              Save
                            </ButtonSmall>
                            <ButtonSecondary
                              type="button"
                              style={{ padding: '0.375rem 0.75rem', fontSize: '0.8125rem' }}
                              onClick={() => setEditingUser(null)}
                            >
                              Cancel
                            </ButtonSecondary>
                          </form>
                        ) : canEdit ? (
                          <ButtonSmall type="button" onClick={() => handleStartEditUser(u)}>
                            Edit
                          </ButtonSmall>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            {canCreate && (
              <>
                <Divider />
                <CardTitle as="h3" style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>
                  Create user
                </CardTitle>
                <form onSubmit={handleCreateUser}>
                  <FormGrid>
                    <FormGroup>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        placeholder="admin@example.com"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={userPassword}
                        onChange={(e) => setUserPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>First name</Label>
                      <Input
                        value={userFirstName}
                        onChange={(e) => setUserFirstName(e.target.value)}
                        placeholder="John"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Last name</Label>
                      <Input
                        value={userLastName}
                        onChange={(e) => setUserLastName(e.target.value)}
                        placeholder="Doe"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Role</Label>
                      <Select value={userRoleId} onChange={(e) => setUserRoleId(e.target.value)}>
                        <option value="">Full access</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </Select>
                    </FormGroup>
                    <FormGroup style={{ alignSelf: 'flex-end' }}>
                      <Button type="submit" disabled={createUserMutation.isLoading}>
                        {createUserMutation.isLoading ? 'Creating…' : 'Create user'}
                      </Button>
                    </FormGroup>
                  </FormGrid>
                </form>
              </>
            )}
          </>
        )}
      </Card>
    </Section>
  );
}
